import { defineStore } from 'pinia';
import { ref } from 'vue';
import { RuntimeConfig } from '@vp/common/api';

declare global {
  interface Window {
    turnstile?: any;
  }
}

/** Thrown when the customer dismisses the Fan Verification modal. Not a failure. */
export class TurnstileCancelledError extends Error {}

export const useTurnstileStore = defineStore('turnstile', () => {
  const ready = ref(false);
  const warmflight = ref<Promise<void> | null>(null);

  const open = ref(false);
  const inflight = ref<Promise<void> | null>(null);

  const humanToken = ref<string | null>(null);

  const resolveCb = ref<(() => void) | null>(null);
  const rejectCb = ref<((reason?: unknown) => void) | null>(null);

  const ensureScript = () => {
    if (typeof window === 'undefined') {
      return Promise.resolve();
    }

    if (window.turnstile) {
      return Promise.resolve();
    }

    return new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
      script.async = true;
      script.defer = true;
      script.onload = () => {
        resolve();
      };
      script.onerror = () => {
        reject(new Error('Failed to load Turnstile script'));
      };
      document.head.appendChild(script);
    });
  };

  const runWarmupOnce = async () => {
    const { turnstile } = await RuntimeConfig.getRuntimeConfig();
    const sitekey = turnstile?.managedKiteKey;
    if (!sitekey) {
      return;
    }

    await ensureScript();

    const container = document.createElement('div');
    container.style.cssText = 'position: absolute; left: -9999px; top: -9999px; width: 1px; height: 1px; overflow: hidden;';
    document.body.appendChild(container);

    let widgetId: string | undefined;
    let token: string | null;
    try {
      token = await new Promise<string | null>((resolve, reject) => {
        const fail = () => reject(new Error('Turnstile error'));
        widgetId = window.turnstile.render(container, {
          sitekey,
          appearance: 'interaction-only',
          retry: 'auto',
          callback: (token: string) => resolve(token),
          'before-interactive-callback': () => resolve(null),
          'error-callback': fail,
          'timeout-callback': fail,
          'unsupported-callback': fail,
        });
      });
    } finally {
      if (widgetId !== undefined) {
        window.turnstile.remove(widgetId);
      }
      container.remove();
    }

    // The off-screen widget cannot be clicked, so the buyer finishes the challenge in the modal.
    if (token === null) {
      return solve();
    }

    await verifyToken(token);
    ready.value = true;
  };

  const verifyToken = async (token: string) => {
    const r = await fetch('/turnstile/verify', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ token }),
    });

    const body = await r.json().catch(() => ({}));

    if (!r.ok || !body?.success) {
      throw new Error(body?.message || 'Token verification failed');
    }

    if (body.token) {
      humanToken.value = body.token;
    }
  };

  const warmup = async () => {
    if (ready.value) {
      return Promise.resolve();
    }

    if (warmflight.value) {
      return warmflight.value;
    }

    warmflight.value = runWarmupOnce()
      .catch(() => { /* The next /api/* call 403s and opens the modal, so the buyer is not stuck. */ })
      .finally(() => warmflight.value = null);

    return warmflight.value;
  };

  const solve = (): Promise<void> => {
    if (inflight.value) {
      return inflight.value;
    }

    open.value = true;
    inflight.value = new Promise<void>((resolve, reject) => {
      resolveCb.value = resolve;
      rejectCb.value = reject;
    }).finally(() => {
      inflight.value = null;
    });

    return inflight.value;
  };

  const resolved = () => {
    open.value = false;
    ready.value = true;
    resolveCb.value?.();
    resolveCb.value = null;
    rejectCb.value = null;
  };

  const failed = (err?: unknown) => {
    open.value = false;
    rejectCb.value?.(err ?? new Error('Turnstile challenge failed'));
    resolveCb.value = null;
    rejectCb.value = null;
  };

  return {
    ready,
    warmflight,

    open,
    inflight,

    humanToken,

    ensureScript,
    verifyToken,
    warmup,
    solve,
    resolved,
    failed,
  };
});
